"use client";
import { Dialog } from "@base-ui/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import AcademyIcon from "@/components/icons/academy";
import BookmarkIcon from "@/components/icons/bookmark";
import LikeIcon from "@/components/icons/like";
import MessageIcon from "@/components/icons/message";
import { Button } from "@/components/ui/button";

export type LoginRequiredAction = "like" | "comment" | "favorite" | "apply";

const ACTION_CONFIG: Record<
  LoginRequiredAction,
  { description: string; dialogIcon: React.ReactNode; triggerIcon: React.ReactNode }
> = {
  apply: {
    description: "Başvuru yapmak için lütfen giriş yapın.",
    dialogIcon: <AcademyIcon className="size-7" />,
    triggerIcon: <AcademyIcon className="size-6" />,
  },
  comment: {
    description: "Yorum yapmak için lütfen giriş yapın.",
    dialogIcon: <MessageIcon className="size-7 stroke-brand-blue-500 stroke-[1.5]" />,
    triggerIcon: (
      <MessageIcon className="stroke-neutral-400 size-6 stroke-[1.5] group-hover:stroke-brand-blue-500" />
    ),
  },
  favorite: {
    description: "Favorilere eklemek için lütfen giriş yapın.",
    dialogIcon: <BookmarkIcon className="size-7 fill-brand-blue-500 stroke-none" />,
    triggerIcon: (
      <BookmarkIcon className="stroke-neutral-400 fill-none size-6 stroke-[1.5] group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100" />
    ),
  },
  like: {
    description: "Beğenmek için lütfen giriş yapın.",
    dialogIcon: <LikeIcon className="size-7 stroke-brand-blue-500 stroke-[1.5]" />,
    triggerIcon: (
      <LikeIcon className="stroke-neutral-400 size-6 stroke-[1.5] group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100" />
    ),
  },
};

interface LoginRequiredDialogProps {
  action: LoginRequiredAction;
  count?: number;
  triggerLabel?: string;
}

const LoginRequiredDialog = ({ action, count, triggerLabel }: LoginRequiredDialogProps) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const config = ACTION_CONFIG[action];

  return (
    <span className="inline-flex items-center justify-center">
      {triggerLabel ? (
        <Button className="w-fit" onClick={() => setOpen(true)}>
          {triggerLabel}
        </Button>
      ) : (
        <Button
          className="group"
          onClick={() => setOpen(true)}
          size="icon-sm"
          variant="ghost"
        >
          {config.triggerIcon}
        </Button>
      )}
      {count !== undefined && count}

      <Dialog.Root onOpenChange={setOpen} open={open}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-100 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
          <Dialog.Popup className="fixed z-100 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center justify-center size-14 rounded-full bg-brand-blue-50">
                {config.dialogIcon}
              </div>
              <div className="flex flex-col gap-1.5">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Giriş Yapmanız Gerekiyor
                </Dialog.Title>
                <Dialog.Description className="text-sm text-gray-500">
                  {config.description}
                </Dialog.Description>
              </div>
              <div className="flex flex-col gap-2 w-full mt-2">
                <Button
                  className="w-full"
                  onClick={() => router.push("/auth/login")}
                >
                  Giriş Yap
                </Button>
                <Dialog.Close
                  className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                  render={<button type="button" />}
                >
                  Vazgeç
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </span>
  );
};

export { LoginRequiredDialog };
